import { FilterOp } from 'common/types/summaryDefinitions';
import { DisplayFormatType } from 'common/types/summaryDefinitions/valueTypes/displayFormats';
import { ValueType } from 'common/types/summaryDefinitions/valueTypes/valueType';

export type FilterOpOption = { value: FilterOp; label: string };

const ALL_FILTER_OPS: FilterOpOption[] = [
  { value: '=', label: 'Equals' },
  { value: '!=', label: 'Not equal' },
  { value: '>', label: 'Greater than' },
  { value: '>=', label: 'Greater or equal' },
  { value: '<', label: 'Less than' },
  { value: '<=', label: 'Less or equal' },
  { value: 'contains', label: 'Contains' },
];

const NUMERIC_OPS: FilterOpOption[] = ALL_FILTER_OPS.filter(o => o.value !== 'contains');

const EQUALITY_OPS: FilterOpOption[] = ALL_FILTER_OPS.filter(o => o.value === '=' || o.value === '!=');

export function getDisplayFormatTypeForColumn(
  column: string,
  valueTypeById: Map<string, ValueType>
): DisplayFormatType {
  return valueTypeById.get(column)?.displayFormat?.type ?? 'text';
}

export function getFilterOpsForColumnType(formatType: DisplayFormatType): FilterOpOption[] {
  switch (formatType) {
    case 'currency':
    case 'percentage':
    case 'number':
    case 'date':
      return NUMERIC_OPS;
    case 'boolean':
      return EQUALITY_OPS;
    case 'text':
    case 'custom':
    default:
      return ALL_FILTER_OPS;
  }
}

export interface FilterValueValidation {
  valid: boolean;
  error?: string;
}

export function validateFilterValue(value: string | number, formatType: DisplayFormatType): FilterValueValidation {
  const str = String(value ?? '');

  if (str === '') {
    return { valid: true };
  }

  switch (formatType) {
    case 'currency':
    case 'percentage':
    case 'number': {
      const num = Number(str);
      if (Number.isNaN(num) || str.trim() === '') {
        return { valid: false, error: 'Must be a number' };
      }
      return { valid: true };
    }
    case 'date': {
      const isoDate = /^\d{4}-\d{2}-\d{2}$/.test(str);
      const usDate = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/\d{4}$/.test(str);
      if (!isoDate && !usDate) {
        return { valid: false, error: 'Must be a valid date (MM/DD/YYYY)' };
      }
      const isoStr = isoDate
        ? str
        : (() => {
          const [month, day, year] = str.split('/');
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        })();
      const parsed = new Date(isoStr);
      if (Number.isNaN(parsed.getTime())) {
        return { valid: false, error: 'Must be a valid date (MM/DD/YYYY)' };
      }
      return { valid: true };
    }
    case 'boolean': {
      const lower = str.toLowerCase();
      if (!['true', 'false', 'yes', 'no'].includes(lower)) {
        return { valid: false, error: 'Must be Yes or No' };
      }
      return { valid: true };
    }
    case 'text':
    case 'custom':
    default:
      return { valid: true };
  }
}

export function isOpValidForFormatType(op: FilterOp, formatType: DisplayFormatType): boolean {
  return getFilterOpsForColumnType(formatType).some(o => o.value === op);
}
